import { ArrowLeft, Save } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso} from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Input } from "./ui/input"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

interface FormData {
  fecha: string
  resultado: string
  realizo: string
  superviso: string
  observaciones: string
  mostrarObservaciones: boolean
  numeroSolicitud?: string // NUEVO: campo número de solicitud
}

interface MET7211FormProps {
  pedidoNumero: string
  pedidoRowId?: string
  onBack: () => void
  onSave: (data: FormData) => void
  realizoUser?: string
  initialData?: Partial<FormData>
  mode?: 'create' | 'view' | 'edit'
  onRefreshBackend?: () => void
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

export function MET7211Form({ pedidoNumero, pedidoRowId, onBack, onSave, realizoUser, initialData = {}, mode = 'create', onRefreshBackend, onAutosave }: MET7211FormProps) {
  const globalRol = typeof useUserRol === 'function' ? useUserRol() : undefined;
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = globalRol;

  const [formData, setFormData] = useState<FormData>({
    fecha: new Date().toISOString().split('T')[0],
    resultado: "",
    realizo: (mode === 'create' && realizoUser) ? realizoUser : initialData.realizo || "",
    superviso: "",
    observaciones: "",
    ...initialData,
    mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
  })

  // Set realizo from prop SOLO en modo create
  useEffect(() => {
    if (mode === 'create' && realizoUser) {
      setFormData(prev => ({ ...prev, realizo: realizoUser }))
    }
  }, [realizoUser, mode])

  const [editMode, setEditMode] = useState(false);
  // Inicializa estatus buscando en varias propiedades posibles
  const initialEstatus = (initialData as any)?.ACEPTAR || (initialData as any)?.ACEPTADO || (initialData as any)?.estatus || '';
  const [estatus, setEstatus] = useState<string>(initialEstatus);
  const [localMode, setLocalMode] = useState<'view' | 'edit' | 'create'>(mode);
  const [rechazoModalOpen, setRechazoModalOpen] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  useEffect(() => {
    if (editMode) {
      setLocalMode('edit');
    } else {
      setLocalMode(mode || 'view');
    }
  }, [editMode, mode]);

  const isReadOnly = localMode === 'create' ? false : (localMode === 'view');

  useEffect(() => {
    if (mode === 'view' && effectiveRol !== 'ADMIN') {
      setEditMode(false);
    }
  }, [effectiveRol, mode]);

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      onSave(formData)
    } finally {
      setGuardando(false)
    }
  }

  const [autosaveRowId, setAutosaveRowId] = useState<string>((initialData as any)?.ROW_ID || (initialData as any)?.row_id || '');
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialData as any)?.nomId || '');
  const rowId = autosaveRowId;
  const nomId = autosaveNomId;
  const pedidoValue = pedidoNumero || '';
  const pruebaValue = 'TIPO DE TEJIDO MET-7.2-11';

  const handleAccion = async (accion: 'aceptado' | 'rechazado', razonRechazo?: string) => {
    const confirmMsg = accion === 'aceptado'
      ? 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba MET-7.2-11?'
      : 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba MET-7.2-11?';
    if (accion === 'rechazado' && !razonRechazo) {
      setRechazoModalOpen(true);
      return;
    }
    if (!window.confirm(confirmMsg)) return;
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: accion === 'aceptado' ? 'aceptado' : 'rechazado',
      ACEPTADO: accion,
      ...(accion === 'rechazado' ? { RAZON_RECHAZO: razonRechazo } : {})
    };
    try {
      if (accion === 'rechazado') setRechazoLoading(true);
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, accion === 'rechazado' ? (payload as any).RAZON_RECHAZO : undefined, (payload as any).superviso, nomId);
      setEstatus(payload.ACEPTAR);
      setEditMode(false);
      setRechazoModalOpen(false);
      setRechazoLoading(false);
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      setRechazoLoading(false);
      alert('Error al enviar al backend: ' + (err.message || err));
    }
  };

  const handleGuardarEdicion = async () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de MET-7.2-11? Esta acción no se puede deshacer.')) return;
    // Copia el formData y ajusta numeroSolicitud
    const payload: any = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado'
    };
    // Forzar numeroSolicitud como string o eliminar si está vacío
    if (payload.numeroSolicitud !== undefined && payload.numeroSolicitud !== null && payload.numeroSolicitud !== '') {
      payload.numeroSolicitud = String(payload.numeroSolicitud);
    } else {
      delete payload.numeroSolicitud;
    }

    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  };

  // Normaliza el estatus para comparación
  const normalizedEstatus = (estatus || '').toString().trim().toLowerCase();

  // --- ADMIN ACTIONS (replicado de AnalisisQuimicoForm) ---
  const isAceptado = normalizedEstatus === 'aceptado' || normalizedEstatus === 'rechazado';

  const formPayload = () => formData;

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'TIPO DE TEJIDO MET-7.2-11',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onBack()
  }

  const handleEditar = () => setEditMode(true);

  // handleAccion y handleGuardarEdicion ya están bien, solo aseguramos consistencia de payload y control de estatus

  // --- Render ---
  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={handleBackFlush}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        <div>
          <h1>DETERMINACIÓN DEL TIPO DE TELAS MET-7.2-11</h1>
          <p className="text-muted-foreground">Pedido: {pedidoNumero}</p>
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-center">DETERMINACIÓN DEL TIPO DE TELAS MET-7.2-11</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-2 border-foreground">
              <tbody>
                <tr>
                  <td className="border-2 border-foreground p-4 font-medium bg-muted w-1/2">
                    FECHA:
                  </td>
                  <td className="border-2 border-foreground p-2 w-1/2">
                    <Input
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => handleInputChange('fecha', e.target.value)}
                      className="border-0 bg-transparent focus:ring-0 focus:outline-none"
                      disabled={isReadOnly}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border-2 border-foreground p-4 font-medium bg-muted w-1/2">
                    NÚMERO DE SOLICITUD:
                  </td>
                  <td className="border-2 border-foreground p-2 w-1/2">
                    <Input
                      type="text"
                      placeholder="Ejemplo: 12345"
                      value={formData.numeroSolicitud || ''}
                      onChange={(e) => handleInputChange('numeroSolicitud', e.target.value)}
                      className="border-0 bg-transparent focus:ring-0 focus:outline-none"
                      disabled={isReadOnly}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border-2 border-foreground p-4 font-medium bg-muted w-1/2">
                    RESULTADO:
                  </td>
                  <td className="border-2 border-foreground p-2 w-1/4">
                    <Input
                      placeholder="RESULTADO"
                      value={formData.resultado}
                      onChange={(e) => handleInputChange('resultado', e.target.value)}
                      className="border-0 bg-transparent focus:ring-0 focus:outline-none"
                      disabled={isReadOnly}
                    />
                  </td>
                  <td className="border-2 border-foreground p-4 font-medium bg-muted w-1/4 text-right align-middle">
                    REALIZÓ:
                  </td>
                  <td className="border-2 border-foreground p-2 w-1/4 align-middle">
                    <div className="rounded bg-gray-50 px-2 py-1 text-center font-semibold text-gray-700 border border-gray-200 min-h-[38px] flex items-center justify-center">
                      {formData.realizo || <span className="text-gray-400">Sin asignar</span>}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="border-2 border-foreground p-4 font-medium bg-muted" colSpan={2}>
                    {/* Espacio vacío para mantener la estructura */}
                  </td>
                  <td className="border-2 border-foreground p-4 font-medium bg-muted w-1/4 text-right align-middle">
                    SUPERVISÓ:
                  </td>
                  <td className="border-2 border-foreground p-2 w-1/4 align-middle">
                    <div className="rounded bg-gray-50 px-2 py-1 text-center font-semibold text-gray-700 border border-gray-200 min-h-[38px] flex items-center justify-center">
                      {formData.superviso || <span className="text-gray-400">Sin asignar</span>}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="border-2 border-foreground p-4 font-medium bg-muted flex items-center gap-2" colSpan={4}>
                    OBSERVACIONES:
                    <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                      Mostrar en Reporte Cliente
                      <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                        onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="border-2 border-foreground p-2" colSpan={4}>
                    <Textarea
                      placeholder="OBSERVACIONES"
                      value={formData.observaciones}
                      onChange={(e) => handleInputChange('observaciones', e.target.value)}
                      className="border-0 bg-transparent focus:ring-0 focus:outline-none min-h-[100px] resize-none"
                      disabled={isReadOnly}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={handleBackFlush}>
          Cancelar
        </Button>
        {/* Botones de acciones en modo view y no aceptado SOLO PARA ADMIN */}
        {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
          <>
            <Button variant="default" onClick={() => handleAccion('aceptado')}>Aceptar</Button>
            <Button variant="destructive" onClick={() => setRechazoModalOpen(true)}>Rechazar</Button>
            <Button variant="secondary" onClick={handleEditar}>Editar</Button>
          </>
        )}
        {/* Botón guardar edición SOLO PARA ADMIN */}
        {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
          <Button variant="default" onClick={handleGuardarEdicion}>
            <Save className="h-4 w-4 mr-2" />
            Guardar Edición
          </Button>
        )}
        {/* Botón guardar en modo create */}
        {mode === 'create' && (
          <Button onClick={handleSave} disabled={guardando}>
            <Save className="h-4 w-4 mr-2" />
            {guardando ? 'Guardando...' : 'Guardar Formulario'}
          </Button>
        )}
      </div>
      <RechazoModal
        open={rechazoModalOpen}
        onClose={() => setRechazoModalOpen(false)}
        loading={rechazoLoading}
        onSubmit={(razon) => handleAccion('rechazado', razon)}
      />
    </div>
  )
}